import { ErrorMessage } from '@hookform/error-message';
import get from 'lodash.get';
import { CSSProperties, InputHTMLAttributes } from 'react';
import {
  DeepMap,
  FieldError,
  FieldValues,
  Path,
  RegisterOptions,
  UseFormRegister,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { BaseInput } from './input';
import createInputCSS from './input-css-factory';

export interface IProps extends InputHTMLAttributes<HTMLInputElement> {
  /** name of the input is required to work properly  */
  name: string;

  /* label text of the input */
  label?: string;

  /* position of the label relative to input. default: left */
  labelPos?: 'left' | 'bottom';

  /* inline css of the label */
  labelStyle?: CSSProperties;

  /* css class name(s) of the label */
  labelStyleClass?: string;

  /* variant of the input style. default: solid */
  variant?: 'plain' | 'underline' | 'solid';
}

export type FormInputProps<TFormValues extends FieldValues> = {
  name: Path<TFormValues>;
  rules?: RegisterOptions;
  register?: UseFormRegister<TFormValues>;
  errors?: Partial<DeepMap<TFormValues, FieldError>>;
} & Omit<IProps, 'name'>;

export const Input = <TFormValues extends Record<string, unknown>>({
  name,
  register,
  rules,
  errors,
  type = 'text',
  label,
  className,
  labelStyle,
  labelStyleClass,
  labelPos = 'left',
  variant = 'solid',
  ...props
}: FormInputProps<TFormValues>): JSX.Element => {
  const { t } = useTranslation();

  // if the name is in a FieldArray, it will be 'fields.index.fieldName' and errors[name] won't return anything,
  // so we are using lodash get the error messages
  const errorMessages = errors ? get(errors, name) : null;
  const hasError = !!(errors && errorMessages);

  // CSS classes will be managed here based on the application state
  const { containerCx, labelCx, inputCx } = createInputCSS({
    variant,
    labelPos,
    labelStyleClass,
    className,
    hasError,
  });

  return (
    <div className={containerCx}>
      {label && (
        <label className={labelCx} style={labelStyle} htmlFor={name}>
          {label}
        </label>
      )}
      <div className="w-full">
        <BaseInput
          name={name}
          type={type}
          className={inputCx}
          aria-invalid={hasError}
          {...props}
          {...(register && register(name, rules))}
        />
        {hasError && (
          <ErrorMessage
            errors={errors}
            name={name as any}
            render={({ message }) => (
              <p className="mt-1 pl-1 text-xs font-medium text-red-light">
                {t(`${message}`)}
              </p>
            )}
          />
        )}
      </div>
    </div>
  );
};
